import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * MORIAI cart drawer extras — upsell ADD buttons, free gift auto add/remove,
 * Subscribe & Save (real selling plans only), and upsell carousel navigation.
 *
 * Uses the exact same event contract as the theme's own components
 * (CartLinesUpdateEvent + Section Rendering API sections), so the drawer,
 * header bubble and totals all refresh through the native pipeline —
 * no page reloads, no parallel cart system.
 */

const CONTEXT = 'moriai-cart';
let inFlight = false;

const routes = () => (window.Theme && window.Theme.routes) || {};

function sectionIds() {
  const ids = new Set(['cart-drawer-section']);
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids).join(',');
}

async function fetchCart() {
  const res = await fetch(`${routes().cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
  return res.json();
}

function showError(message) {
  const el = document.querySelector('[data-moriai-error]');
  if (!(el instanceof HTMLElement)) return;
  el.textContent = message || 'Something went wrong. Please try again.';
  el.hidden = false;
  setTimeout(() => {
    el.hidden = true;
  }, 5000);
}

/**
 * Adds items to the cart via /cart/add.js and notifies the theme.
 * @param {Element} target - DOM element to dispatch the event from.
 * @param {Array<{id: number, quantity: number, properties?: object}>} items
 */
async function addLines(target, items) {
  if (inFlight) return { ok: false, silent: true };
  inFlight = true;

  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: CONTEXT,
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(routes().cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds(), sections_url: window.location.pathname }),
    });
    const data = await res.json();

    if (data.status) {
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: 0, source: CONTEXT, didError: true },
      });
      return { ok: false, message: data.message || data.description };
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: items.reduce((sum, i) => sum + i.quantity, 0),
        source: CONTEXT,
        sections: data.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: 'Network error. Please try again.' };
  } finally {
    inFlight = false;
  }
}

/**
 * Changes a cart line via /cart/change.js and notifies the theme.
 * @param {Element} target
 * @param {{id: string, quantity: number, selling_plan?: number | null}} body
 * @param {'update' | 'remove'} action
 */
async function changeLine(target, body, action) {
  if (inFlight) return { ok: false, silent: true };
  inFlight = true;

  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: CONTEXT,
      lines: [{ id: body.id, quantity: body.quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(routes().cart_change_url || '/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionIds(), sections_url: window.location.pathname }),
    });
    const data = await res.json();

    if (data.errors) {
      deferred.reject(new Error(typeof data.errors === 'string' ? data.errors : 'Cart update failed'));
      return { ok: false, message: typeof data.errors === 'string' ? data.errors : undefined };
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        items: data.items,
        itemCount: data.item_count,
        source: CONTEXT,
        sections: data.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: 'Network error. Please try again.' };
  } finally {
    inFlight = false;
  }
}

/* ============ Upsell: ADD button ============ */

async function onUpsellAdd(button) {
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;
  button.disabled = true;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  const variantId = Number(button.dataset.variantId);
  const result = await addLines(button, [{ id: variantId, quantity: 1 }]);

  // On success the section re-renders and this button disappears (the product
  // is now in the cart). On failure, restore the button and surface the error.
  if (!result.ok && !result.silent) showError(result.message);
  if (button.isConnected) {
    button.disabled = false;
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  }
}

/* ============ Free gift ============ */

const GIFT_DISMISSED_KEY = 'moriai_gift_dismissed';

function giftState() {
  const el = document.querySelector('[data-moriai-cart]');
  if (!(el instanceof HTMLElement)) return null;
  const d = el.dataset;
  return {
    el,
    enabled: d.giftEnabled === 'true',
    variant: Number(d.giftVariant || 0),
    eligible: d.giftEligible === 'true',
    inCart: d.giftInCart === 'true',
    lineKey: d.giftLineKey || '',
    qty: Number(d.giftQty || 0),
    removeBelow: d.giftRemoveBelow === 'true',
  };
}

async function syncGift() {
  if (inFlight) return;
  const s = giftState();
  if (!s || !s.enabled || !s.variant) return;

  const dismissed = sessionStorage.getItem(GIFT_DISMISSED_KEY) === '1';
  // Reset the manual-dismiss flag once the cart drops below the threshold,
  // so the gift re-appears the next time the customer qualifies again.
  if (!s.eligible && dismissed) sessionStorage.removeItem(GIFT_DISMISSED_KEY);

  if (s.eligible && !s.inCart && !dismissed) {
    await addLines(s.el, [{ id: s.variant, quantity: 1, properties: { _moriai_gift: '1' } }]);
  } else if (!s.eligible && s.inCart && s.removeBelow && s.lineKey) {
    await changeLine(s.el, { id: s.lineKey, quantity: 0 }, 'remove');
  } else if (s.inCart && s.qty > 1 && s.lineKey) {
    // The gift can never be duplicated: force quantity back to 1.
    await changeLine(s.el, { id: s.lineKey, quantity: 1 }, 'update');
  }
}

async function onGiftRemove(button) {
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;
  button.disabled = true;
  // Remember the customer's choice for this session so the gift is not
  // immediately re-added while they still qualify.
  sessionStorage.setItem(GIFT_DISMISSED_KEY, '1');
  const result = await changeLine(button, { id: button.dataset.lineKey || '', quantity: 0 }, 'remove');
  if (!result.ok && !result.silent) {
    showError(result.message);
    if (button.isConnected) button.disabled = false;
  }
}

/* ============ Subscribe & Save (real selling plans only) ============ */

function onSubscriptionChange(input) {
  const freq = document.querySelector('.moriai-sub__freq');
  const select = document.querySelector('[data-moriai-plan-select]');
  const lineKey = input.dataset.lineKey || '';
  const qty = Number(input.dataset.lineQty || 1);

  if (input.hasAttribute('data-moriai-sub-toggle')) {
    const checked = input instanceof HTMLInputElement && input.checked;
    if (freq instanceof HTMLElement) freq.classList.toggle('is-hidden', !checked);
    const planId = checked
      ? Number((select instanceof HTMLSelectElement && select.value) || input.dataset.defaultPlan || 0)
      : null;
    changeLine(input, { id: lineKey, quantity: qty, selling_plan: planId }, 'update').then((r) => {
      if (!r.ok && !r.silent) showError(r.message);
    });
    return;
  }

  if (input.hasAttribute('data-moriai-plan-select') && input instanceof HTMLSelectElement) {
    changeLine(input, { id: lineKey, quantity: qty, selling_plan: Number(input.value) }, 'update').then((r) => {
      if (!r.ok && !r.silent) showError(r.message);
    });
  }
}

/* ============ Carousel navigation ============ */

function onScrollNav(button) {
  const row = button.closest('.moriai-upsell__row');
  const scroller = row && row.querySelector('[data-moriai-scroller]');
  if (!(scroller instanceof HTMLElement)) return;
  const direction = button.getAttribute('data-moriai-scroll') === 'next' ? 1 : -1;
  scroller.scrollBy({ left: direction * scroller.clientWidth, behavior: 'smooth' });
}

/* ============ Wiring (delegated — survives section morphs) ============ */

if (!window.__moriaiCartInit) {
  window.__moriaiCartInit = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest('[data-moriai-upsell-add]');
    if (addButton) return void onUpsellAdd(addButton);

    const giftRemove = target.closest('[data-moriai-gift-remove]');
    if (giftRemove) return void onGiftRemove(giftRemove);

    const nav = target.closest('[data-moriai-scroll]');
    if (nav) return void onScrollNav(nav);
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.hasAttribute('data-moriai-sub-toggle') || target.hasAttribute('data-moriai-plan-select')) {
      onSubscriptionChange(/** @type {HTMLElement} */ (target));
    }
  });

  // Re-check gift eligibility after every cart mutation, once the section
  // has been re-rendered (the fresh DOM carries the new eligibility flags).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;
    promise.then(() => setTimeout(syncGift, 200)).catch(() => {});
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(syncGift, 400));
  } else {
    setTimeout(syncGift, 400);
  }
}
